import React, { useState } from 'react';
import { X } from 'lucide-react';
import KPICard from '../../components/shared/KPICard';
import SectionDivider from '../../components/shared/SectionDivider';
import GujaratMap from './GujaratMap';
import { gujaratStateKPIs, gujaratDistrictAnalyser } from '../../data/mockData';

const prCls = (v: number) =>
  v >= 75 ? 'text-green-600 font-semibold' :
  v >= 65 ? 'text-amber-600 font-semibold' :
            'text-red-600 font-semibold';

function PanelRow({ label, value, cls }: { label: string; value: string; cls?: string }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-border/40 last:border-0">
      <span className="text-xs text-text-tertiary">{label}</span>
      <span className={`text-xs font-semibold text-right ${cls ?? 'text-text-primary'}`}>{value}</span>
    </div>
  );
}

// State-level summary values
const stateSummary = {
  totalPlants:        gujaratStateKPIs.plants,
  capacityMWp:        gujaratStateKPIs.capacityGWp * 1000,
  generationMWh:      gujaratStateKPIs.generationGWh * 1000,
  specificYield:      (gujaratStateKPIs.generationGWh * 1000 / (gujaratStateKPIs.capacityGWp * 1000)).toFixed(2),
  avgPR:              gujaratStateKPIs.avgPR,
  avgCUF:             gujaratStateKPIs.avgCufPct,
  plantAvailability:  gujaratStateKPIs.activePercent,
  activePlants:       Math.round(gujaratStateKPIs.plants * gujaratStateKPIs.activePercent / 100),
  offlinePlants:      gujaratStateKPIs.offlineFaultCount,
  activeAlarms:       gujaratStateKPIs.alarms,
};

export default function StateOverview() {
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null);

  const districtData = selectedDistrict
    ? gujaratDistrictAnalyser.find(d => d.name === selectedDistrict)
    : null;

  const panel = districtData ? {
    totalPlants:       districtData.plants,
    capacityMWp:       districtData.capacityMWp,
    generationMWh:     districtData.generationMWh,
    specificYield:     districtData.specificYield.toFixed(2),
    avgPR:             districtData.prPercent,
    avgCUF:            districtData.cufPercent,
    plantAvailability: districtData.availPercent,
    activePlants:      Math.round(districtData.plants * districtData.availPercent / 100),
    offlinePlants:     districtData.plants - Math.round(districtData.plants * districtData.availPercent / 100),
    activeAlarms:      districtData.alarms,
  } : {
    totalPlants:       stateSummary.totalPlants,
    capacityMWp:       stateSummary.capacityMWp,
    generationMWh:     stateSummary.generationMWh,
    specificYield:     stateSummary.specificYield,
    avgPR:             stateSummary.avgPR,
    avgCUF:            stateSummary.avgCUF,
    plantAvailability: stateSummary.plantAvailability,
    activePlants:      stateSummary.activePlants,
    offlinePlants:     stateSummary.offlinePlants,
    activeAlarms:      stateSummary.activeAlarms,
  };

  return (
    <div className="p-6 space-y-5">
      {/* ── 6 primary KPI cards ── */}
      <div className="flex gap-4">
        <KPICard title="Total Plants"          value={gujaratStateKPIs.plants.toLocaleString()}                      subtext="Across Gujarat" />
        <KPICard title="Installed Capacity"    value={`${gujaratStateKPIs.capacityGWp} GWp`}                         subtext="Installed" />
        <KPICard title="Today's Generation"    value={`${gujaratStateKPIs.generationGWh} GWh`}                       subtext="+3.2% vs yesterday" subtextColor="green" />
        <KPICard title="Active Plants"         value={`${gujaratStateKPIs.activePercent}%`}                           subtext={`${stateSummary.activePlants.toLocaleString()} of ${gujaratStateKPIs.plants.toLocaleString()}`} subtextColor="green" />
        <KPICard title="Offline / Fault"       value={gujaratStateKPIs.offlineFaultCount.toLocaleString()}            subtext="Needs review" subtextColor="red" bgColor="bg-red-50" />
        <KPICard title="Avg Performance Ratio" value={`${gujaratStateKPIs.avgPR}%`}                                   subtext="Gujarat — Good zone" subtextColor="green" />
      </div>

      {/* ── 2-col: map (65%) + detail panel (35%) ── */}
      <div className="flex gap-4" style={{ minHeight: 420 }}>
        {/* District heatmap */}
        <div className="bg-white rounded-card border border-border shadow-card p-4" style={{ width: '65%' }}>
          <SectionDivider
            title="Gujarat District Heatmap"
            subtitle={selectedDistrict ? `Selected: ${selectedDistrict} — click to deselect` : 'Click a district to view details'}
          />
          <div style={{ height: 360 }}>
            <GujaratMap
              onDistrictSelect={setSelectedDistrict}
              selectedDistrict={selectedDistrict}
            />
          </div>
        </div>

        {/* Detail panel */}
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex flex-col" style={{ width: '35%' }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">
              {selectedDistrict ? selectedDistrict : 'Gujarat Summary'}
            </h3>
            {selectedDistrict && (
              <button
                onClick={() => setSelectedDistrict(null)}
                className="text-text-tertiary hover:text-text-primary transition-colors"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div className="flex-1 space-y-0">
            <PanelRow label="Total Plants"       value={panel.totalPlants.toLocaleString()} />
            <PanelRow label="Installed Capacity" value={`${panel.capacityMWp.toFixed(0)} MWp`} />
            <PanelRow label="Today's Generation" value={`${panel.generationMWh.toLocaleString()} MWh`} />
            <PanelRow label="Specific Yield"     value={`${panel.specificYield} kWh/kWp`} />
            <PanelRow
              label="Avg PR"
              value={`${panel.avgPR.toFixed(1)}%`}
              cls={prCls(panel.avgPR)}
            />
            <PanelRow label="Avg CUF"            value={`${panel.avgCUF.toFixed(1)}%`} />
            <PanelRow label="Plant Availability"  value={`${panel.plantAvailability.toFixed(0)}%`} />
            <PanelRow label="Active Plants"       value={panel.activePlants.toLocaleString()} />
            <PanelRow
              label="Offline Plants"
              value={panel.offlinePlants.toLocaleString()}
              cls={panel.offlinePlants > 0 ? 'text-red-600 font-semibold' : 'text-text-primary'}
            />
            <PanelRow
              label="Active Alarms"
              value={panel.activeAlarms.toString()}
              cls={panel.activeAlarms > 0 ? 'text-red-600 font-semibold' : 'text-text-primary'}
            />
          </div>

          {!selectedDistrict && (
            <p className="text-xs text-text-tertiary mt-3 pt-3 border-t border-border/40">
              Click any district on the map to see district-level details.
            </p>
          )}
        </div>
      </div>

      {/* ── 3 secondary KPI cards ── */}
      <div className="flex gap-4">
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
          <p className="text-xs text-text-secondary font-semibold uppercase tracking-wide">Avg CUF Today</p>
          <p className="text-xl font-bold text-text-primary mt-1">{gujaratStateKPIs.avgCufPct}%</p>
          <p className="text-xs text-text-secondary mt-1">Peak 10am–3pm</p>
        </div>
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
          <p className="text-xs text-text-secondary font-semibold uppercase tracking-wide">Avg Specific Yield</p>
          <p className="text-xl font-bold text-text-primary mt-1">
            {(gujaratStateKPIs.generationGWh * 1000 / (gujaratStateKPIs.capacityGWp * 1000)).toFixed(2)} kWh/kWp
          </p>
          <p className="text-xs text-text-secondary mt-1">Gujarat today</p>
        </div>
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
          <p className="text-xs text-text-secondary font-semibold uppercase tracking-wide">Plant Availability</p>
          <p className="text-xl font-bold text-green-600 mt-1">{gujaratStateKPIs.activePercent}%</p>
          <p className="text-xs text-text-secondary mt-1">Solar hours basis</p>
        </div>
      </div>
    </div>
  );
}
